import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * Protected route wrapper - checks token validity and optional permissions
 * 
 * @param {React.ReactNode} children - Content to render if authorized
 * @param {string|string[]} requiredPermission - Single permission or array of permissions required
 * @param {boolean} requireAll - If true with array, requires all permissions. Default: false (any)
 * @param {string} redirectTo - Where to redirect if unauthorized. Default: /login or /dashboard
 */
export default function ProtectedRoute({ 
  children, 
  requiredPermission = null,
  requireAll = false,
  redirectTo = null
}) {
  const { isValid, hasPermission, hasAnyPermission, hasAllPermissions, loading } = useAuth();
  
  // Show nothing while loading
  if (loading) {
    return null;
  }

  // Not authenticated - redirect to login
  if (!isValid) {
    return <Navigate to="/login" replace />;
  }

  // No permission requirement - just need to be authenticated
  if (!requiredPermission) {
    return children;
  }

  // Check permissions
  let hasAccess = false;

  if (typeof requiredPermission === 'string') {
    // Single permission check
    hasAccess = hasPermission(requiredPermission);
  } else if (Array.isArray(requiredPermission)) {
    // Multiple permissions check
    if (requireAll) {
      hasAccess = hasAllPermissions(...requiredPermission);
    } else {
      hasAccess = hasAnyPermission(...requiredPermission);
    }
  }

  if (!hasAccess) {
    const defaultRedirect = redirectTo || '/dashboard';
    return <Navigate to={defaultRedirect} replace />;
  }
  
  return children;
}
